import React, { useEffect, useState } from 'react';
import { SlashLink as Link } from './SlashLink';
import {
  ANCHOR_PRICE_USD,
  FOUNDING_OFFER_END_LABEL,
  foundingBadgeLabel,
  foundingTierStepLine,
  getDynamicPricing,
  getFoundingOfferState,
} from '../constants/pricing';
import FoundingCountdownTimer from './FoundingCountdownTimer';

function tierStepCopy(pricing) {
  if (!pricing.active || pricing.flash) return null;
  if (!(pricing.nextPriceUsd < ANCHOR_PRICE_USD)) return null;
  return {
    kicker: 'Price steps up',
    line: foundingTierStepLine(),
    next: `$${pricing.nextPriceUsd} lifetime from ${pricing.nextPriceLabel}`,
  };
}

function FoundingUrgencyPaywall({ className, pricing }) {
  const badge = foundingBadgeLabel();
  const tier = tierStepCopy(pricing);
  return (
    <aside className={`founding-urgency-paywall${className ? ` ${className}` : ''}`} aria-label={badge}>
      <div className="founding-urgency-paywall-price">
        <span className="price-strike">${ANCHOR_PRICE_USD}</span>
        <strong>
          ${pricing.priceUsd} lifetime{pricing.flash ? ' Labor Day' : ' founding'}
        </strong>
      </div>
      <p className="founding-urgency-paywall-timer-copy">
        {pricing.flash
          ? `Ends ${pricing.flash.endsLabel} · then $${pricing.nextPriceUsd} lifetime`
          : tier
            ? tier.line
            : `List price $${ANCHOR_PRICE_USD} lifetime after ${FOUNDING_OFFER_END_LABEL}`}
      </p>
    </aside>
  );
}

function FoundingUrgencyHero({ className, pricing }) {
  const badge = foundingBadgeLabel();
  const tier = tierStepCopy(pricing);
  const tierUrgent = !!tier && pricing.msUntilPriceIncrease > 0 && pricing.msUntilPriceIncrease <= 72 * 3600000;
  return (
    <aside
      className={`founding-urgency-strip${tierUrgent ? ' is-tier-urgent' : ''}${className ? ` ${className}` : ''}`}
      aria-label={badge}
    >
      <div className="founding-urgency-strip-body">
        <div className="founding-urgency-strip-price">
          <p className="founding-urgency-strip-eyebrow">{badge}</p>
          <p className="founding-urgency-strip-amount">
            <span className="price-strike">${ANCHOR_PRICE_USD}</span>
            <strong>${pricing.priceUsd} lifetime</strong>
          </p>
        </div>
        <p className="founding-urgency-strip-timer-copy">
          {pricing.flash
            ? `Labor Day only — ends ${pricing.flash.endsLabel} · then $${pricing.nextPriceUsd} lifetime`
            : tier
              ? tier.line
              : `Then $${ANCHOR_PRICE_USD} list · through ${FOUNDING_OFFER_END_LABEL}`}
        </p>
      </div>

      {tier ? (
        <div className={`founding-urgency-strip-timer${tierUrgent ? ' is-imminent' : ''}`}>
          <p className="founding-urgency-strip-timer-copy">{tier.next}</p>
          <FoundingCountdownTimer target="tier" showSeconds={tierUrgent} compact />
        </div>
      ) : null}

      <div className="founding-urgency-strip-foot">
        <p className="founding-urgency-strip-note">Pay once for lifetime · does not auto-renew</p>
        <Link className="founding-urgency-strip-link" to="/pricing#purchase">
          Purchase ${pricing.priceUsd} lifetime
        </Link>
      </div>
    </aside>
  );
}

function FoundingUrgencyFull({ className, pricing, state, showCta, showPrice }) {
  const offerUrgent = state.daysLeft <= 7 || !!pricing.flash;
  const badge = foundingBadgeLabel();
  const tier = tierStepCopy(pricing);
  const tierUrgent = !!tier && pricing.msUntilPriceIncrease > 0 && pricing.msUntilPriceIncrease <= 72 * 3600000;

  return (
    <aside
      className={`founding-urgency-card${offerUrgent ? ' is-urgent' : ''}${
        tierUrgent ? ' is-tier-urgent' : ''
      }${className ? ` ${className}` : ''}`}
      aria-label={`${badge} countdown`}
    >
      {showPrice ? (
        <div className="founding-urgency-head">
          <p className="founding-urgency-eyebrow">{badge}</p>
          <p className="founding-urgency-price">
            <span className="price-strike">${ANCHOR_PRICE_USD}</span>
            <strong>
              ${pricing.priceUsd} lifetime{pricing.flash ? '' : ' founding'}
            </strong>
          </p>
          <p className="founding-urgency-lock">
            {pricing.flash
              ? `Ends ${pricing.flash.endsLabel} · then $${pricing.nextPriceUsd} founding`
              : tier
                ? tier.line
                : `$${pricing.priceUsd} lifetime founding · then $${ANCHOR_PRICE_USD} list`}
          </p>
        </div>
      ) : null}

      {tier ? (
        <div className={`founding-urgency-tier${tierUrgent ? ' is-imminent' : ''}`}>
          <p className="founding-urgency-tier-kicker">{tier.kicker}</p>
          <p className="founding-urgency-tier-copy">{tier.next}</p>
          <FoundingCountdownTimer
            className="founding-urgency-offer-timer"
            target="tier"
            showSeconds={tierUrgent}
          />
        </div>
      ) : (
        <div className="founding-urgency-offer">
          <p className="founding-urgency-offer-kicker">
            {pricing.flash ? 'Labor Day flash ends' : 'Founding window through'}
          </p>
          <p className="founding-urgency-offer-copy">
            {pricing.flash ? pricing.flash.endsLabel : FOUNDING_OFFER_END_LABEL}
          </p>
          <FoundingCountdownTimer
            className="founding-urgency-offer-timer"
            target={pricing.flash ? 'tier' : 'offer'}
            showSeconds={!!pricing.flash}
          />
        </div>
      )}

      {showCta ? (
        <Link className="btn-primary founding-urgency-cta" to="/pricing#purchase">
          Purchase — ${pricing.priceUsd} lifetime
        </Link>
      ) : null}
    </aside>
  );
}

export default function FoundingOfferUrgency({
  className = '',
  variant = 'full',
  showCta = false,
  /** Off where the page hero already shows the founding vs list price (avoid saying it twice). */
  showPrice = true,
}) {
  const [, setTick] = useState(0);
  const pricing = getDynamicPricing();
  const state = getFoundingOfferState();

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!pricing.active) return null;

  if (variant === 'hero') {
    return <FoundingUrgencyHero className={className} pricing={pricing} />;
  }

  if (variant === 'paywall') {
    return <FoundingUrgencyPaywall className={className} pricing={pricing} />;
  }

  return (
    <FoundingUrgencyFull
      className={className}
      pricing={pricing}
      state={state}
      showCta={showCta}
      showPrice={showPrice}
    />
  );
}
